import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { SeasonBadge } from './SeasonBadge';
import { winterPosts } from '../data/winterPosts';

const fallPosts = [
  {
    id: 'fall-furnace-maintenance-checklist',
    title: '10-Point Fall Furnace Maintenance Checklist for Minnesota Homeowners',
    excerpt: 'Before the bitter Minnesota winter arrives, make sure your furnace is ready to keep your family warm. Our expert technicians share the essential maintenance steps every homeowner should know.',
    content: `
      <p>As fall settles into the Twin Cities, it's time to prepare your home's heating system for the long Minnesota winter ahead. A well-maintained furnace not only keeps your family comfortable but also operates more efficiently, saving you money on energy bills.</p>
      
      <h2>Why Fall Furnace Maintenance Matters</h2>
      <p>The worst time to discover your furnace has problems is during a -20°F cold snap. Regular maintenance helps identify issues before they become costly breakdowns and extends the life of your equipment.</p>
      
      <h2>Your 10-Point Furnace Checklist</h2>
      <ol>
        <li><strong>Replace or clean the air filter</strong> - A dirty filter restricts airflow and makes your furnace work harder</li>
        <li><strong>Test the thermostat</strong> - Make sure it's reading accurately and responding properly</li>
        <li><strong>Inspect the blower motor</strong> - Listen for unusual noises or vibrations</li>
        <li><strong>Check the pilot light or ignition</strong> - Should be a steady blue flame</li>
        <li><strong>Examine the heat exchanger</strong> - Cracks can lead to carbon monoxide leaks</li>
        <li><strong>Clean the burners</strong> - Dirty burners reduce efficiency</li>
        <li><strong>Test safety controls</strong> - Ensure all safety features are working</li>
        <li><strong>Inspect the flue pipe</strong> - Check for blockages or damage</li>
        <li><strong>Lubricate moving parts</strong> - Reduces friction and wear</li>
        <li><strong>Test carbon monoxide detectors</strong> - Critical for family safety</li>
      </ol>
      
      <h2>When to Call a Professional</h2>
      <p>While homeowners can handle basic tasks like filter changes, a professional inspection is recommended annually. Our licensed technicians can identify issues you might miss and ensure your system is operating safely and efficiently.</p>
      
      <p><strong>Schedule your fall furnace tune-up today!</strong> Call Bruce Nelson Plumbing & Heating at 651-738-9354.</p>
    `,
    image: 'https://images.pexels.com/photos/7859953/pexels-photo-7859953.jpeg?auto=compress&cs=tinysrgb&w=800',
    date: 'October 15, 2026',
    readTime: '5 min read',
    category: 'Heating',
    season: 'fall'
  },
  {
    id: 'water-heater-winter-prep',
    title: 'Preparing Your Water Heater for Winter: Avoid Cold Showers & High Bills',
    excerpt: 'Cold incoming water temperatures mean your water heater works overtime in winter. Learn how to optimize your water heater for efficiency and reliability during the coldest months.',
    content: `
      <p>When winter arrives in Minnesota, the water entering your home from the city supply can drop to near-freezing temperatures. This means your water heater has to work much harder to deliver the hot water your family needs.</p>
      
      <h2>How Winter Affects Your Water Heater</h2>
      <p>In summer, incoming water might be 55-60°F. In winter, it can drop to 35-40°F. That's an extra 20+ degrees your water heater must overcome, increasing energy consumption by 10-15%.</p>
      
      <h2>Winter Water Heater Maintenance Tips</h2>
      
      <h3>1. Flush the Tank</h3>
      <p>Sediment buildup reduces efficiency. Draining a few gallons from the bottom of your tank removes sediment and improves heat transfer.</p>
      
      <h3>2. Check the Anode Rod</h3>
      <p>This sacrificial rod protects your tank from corrosion. If it's heavily corroded, replacement can extend your water heater's life by years.</p>
      
      <h3>3. Insulate the Tank</h3>
      <p>An insulating blanket can reduce heat loss and save 7-16% on water heating costs. Just be careful not to cover the thermostat, burner, or top of gas water heaters.</p>
      
      <h3>4. Insulate Hot Water Pipes</h3>
      <p>Pipe insulation reduces heat loss as hot water travels to your faucets, meaning you get hot water faster and waste less.</p>
      
      <h3>5. Lower the Temperature (Slightly)</h3>
      <p>If your water heater is set above 120°F, consider lowering it. Every 10°F reduction saves 3-5% on water heating costs.</p>
      
      <h2>Signs Your Water Heater Needs Attention</h2>
      <ul>
        <li>Rusty or discolored hot water</li>
        <li>Strange noises (popping, rumbling)</li>
        <li>Lukewarm water or running out quickly</li>
        <li>Visible leaks or moisture around the unit</li>
        <li>The unit is over 10 years old</li>
      </ul>
      
      <p><strong>Need water heater service?</strong> Contact Bruce Nelson Plumbing & Heating at 651-738-9354 for expert maintenance and repairs.</p>
    `,
    image: 'https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=800',
    date: 'October 8, 2026',
    readTime: '6 min read',
    category: 'Water Heaters',
    season: 'fall'
  },
  {
    id: 'prevent-frozen-pipes-guide',
    title: 'How to Prevent Frozen Pipes This Winter: A Complete Guide',
    excerpt: 'Frozen pipes can burst and cause thousands of dollars in water damage. Learn the preventive measures every Minnesota homeowner should take before temperatures plummet.',
    content: `
      <p>Every winter, thousands of Minnesota homeowners deal with the nightmare of frozen and burst pipes. The good news? Most frozen pipe disasters are preventable with proper preparation.</p>
      
      <h2>Why Pipes Freeze</h2>
      <p>Water expands as it freezes, creating tremendous pressure inside pipes. When this pressure exceeds the pipe's strength, it ruptures. Even a small crack can release hundreds of gallons of water.</p>
      
      <h2>High-Risk Areas in Your Home</h2>
      <ul>
        <li>Exterior walls with plumbing</li>
        <li>Unheated basements and crawl spaces</li>
        <li>Garages with water supply lines</li>
        <li>Kitchen cabinets on exterior walls</li>
        <li>Outdoor hose bibs</li>
      </ul>
      
      <h2>Prevention Steps</h2>
      
      <h3>Before Winter Arrives</h3>
      <ul>
        <li><strong>Disconnect garden hoses</strong> - Water left in hoses can freeze back into the pipe</li>
        <li><strong>Shut off outdoor faucets</strong> - Use the interior shut-off valve and drain the line</li>
        <li><strong>Insulate exposed pipes</strong> - Use foam pipe insulation or heat tape</li>
        <li><strong>Seal air leaks</strong> - Check for gaps around pipes where they enter your home</li>
        <li><strong>Know your shut-off valve location</strong> - Be ready to act fast if a pipe bursts</li>
      </ul>
      
      <h3>During Cold Snaps</h3>
      <ul>
        <li><strong>Let faucets drip</strong> - Moving water is less likely to freeze</li>
        <li><strong>Open cabinet doors</strong> - Let warm air reach pipes under sinks</li>
        <li><strong>Maintain heating</strong> - Keep your home at least 55°F, even when away</li>
        <li><strong>Use space heaters carefully</strong> - In problem areas like crawl spaces</li>
      </ul>
      
      <h2>What to Do If Pipes Freeze</h2>
      <ol>
        <li>Keep the faucet open - As ice melts, water needs to flow</li>
        <li>Apply heat to the frozen section - Use a hair dryer, heating pad, or towels soaked in hot water</li>
        <li>Never use open flame - Torches can damage pipes or start fires</li>
        <li>Check all faucets - If one is frozen, others might be too</li>
        <li>Call a plumber if you can't locate or thaw the frozen section</li>
      </ol>
      
      <p><strong>Need help winterizing your plumbing?</strong> Call Bruce Nelson Plumbing & Heating at 651-738-9354.</p>
    `,
    image: 'https://images.unsplash.com/photo-1767455529565-2f5f816e18d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    date: 'October 1, 2026',
    readTime: '7 min read',
    category: 'Plumbing',
    season: 'fall'
  },
  {
    id: 'boiler-vs-furnace-minnesota',
    title: 'Boiler vs. Furnace: Which Heating System is Best for Minnesota Homes?',
    excerpt: 'Choosing between a boiler and furnace? We break down the pros and cons of each heating system to help you make the best decision for your Twin Cities home.',
    content: `
      <p>When it's time to replace your heating system, Minnesota homeowners often face a choice: boiler or furnace? Both can keep your home comfortable through our brutal winters, but they work very differently.</p>
      
      <h2>How They Work</h2>
      
      <h3>Furnaces</h3>
      <p>Furnaces heat air and distribute it through ductwork. They're the most common heating system in newer homes and can run on natural gas, propane, oil, or electricity.</p>
      
      <h3>Boilers</h3>
      <p>Boilers heat water and distribute it through radiators, baseboard heaters, or radiant floor systems. They're common in older Minnesota homes and typically run on natural gas or oil.</p>
      
      <h2>Comparing the Two</h2>
      
      <h3>Comfort</h3>
      <p><strong>Boilers win.</strong> Radiant heat feels more natural and even. Furnaces can create hot and cold spots, and the forced air can feel drafty.</p>
      
      <h3>Air Quality</h3>
      <p><strong>Boilers win.</strong> Furnaces blow air (and dust, allergens, and germs) through your home. Boilers don't circulate air at all.</p>
      
      <h3>Efficiency</h3>
      <p><strong>Tie.</strong> Modern versions of both can achieve 95%+ efficiency ratings. However, boilers maintain efficiency longer as they age.</p>
      
      <h3>Installation Cost</h3>
      <p><strong>Furnaces win.</strong> Furnaces typically cost less to install, especially if ductwork already exists. Boilers and radiant systems are more expensive upfront.</p>
      
      <h3>Maintenance</h3>
      <p><strong>Boilers win.</strong> Boilers have fewer moving parts and typically require less maintenance. They also tend to last longer (20-30 years vs. 15-20 for furnaces).</p>
      
      <h3>Noise</h3>
      <p><strong>Boilers win.</strong> Boilers operate nearly silently. Furnaces produce noise from the blower and air movement.</p>
      
      <h2>Our Recommendation</h2>
      <p>For many Minnesota homes, especially older ones with existing radiators, boilers remain an excellent choice. For newer homes with ductwork (especially if you want central AC), a furnace makes sense.</p>
      
      <p>The best choice depends on your home's existing infrastructure, your budget, and your comfort preferences.</p>
      
      <p><strong>Need help deciding?</strong> Our heating experts can evaluate your home and recommend the best solution. Call Bruce Nelson Plumbing & Heating at 651-738-9354.</p>
    `,
    image: 'https://images.pexels.com/photos/4819052/pexels-photo-4819052.jpeg?auto=compress&cs=tinysrgb&w=800',
    date: 'September 24, 2026',
    readTime: '6 min read',
    category: 'Heating',
    season: 'fall'
  }
];

export const blogPosts = [...winterPosts, ...fallPosts];

const BlogCard = ({ post, featured = false }) => {
  return (
    <Link 
      to={`/blog/${post.id}`}
      data-testid={`blog-card-${post.id}`}
      className={`group block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 ${featured ? 'md:flex' : ''}`}
    >
      <div className={`relative overflow-hidden ${featured ? 'md:w-1/2' : 'h-48'}`}>
        <img 
          src={post.image} 
          alt={post.title}
          className={`w-full object-cover group-hover:scale-105 transition-transform duration-500 ${featured ? 'h-64 md:h-full' : 'h-48'}`}
        />
        <div className="absolute top-3 left-3">
          <SeasonBadge season={post.season} />
        </div>
      </div>
      <div className={`p-5 ${featured ? 'md:w-1/2 md:p-8 flex flex-col justify-center' : ''}`}>
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {post.date}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {post.readTime}
          </span>
        </div>
        <span className="text-xs font-bold text-[#F58220] uppercase tracking-wider mb-2 block">
          {post.category}
        </span>
        <h3 className={`font-oswald font-bold text-[#14366C] group-hover:text-[#F58220] transition-colors mb-3 ${featured ? 'text-2xl md:text-3xl' : 'text-xl'}`}>
          {post.title}
        </h3>
        <p className="text-gray-600 mb-4 line-clamp-3">
          {post.excerpt}
        </p>
        <span className="inline-flex items-center text-[#F58220] font-semibold group-hover:gap-2 transition-all">
          Read More <ArrowRight className="w-4 h-4 ml-1" />
        </span>
      </div>
    </Link>
  );
};

export default BlogCard;
